import { IconButton } from "@mui/material";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Eye, EyeOff } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import TransparentLoader from "../../components/transparent-loader/TransparentLoader";
import { getSpinnerData } from "../../redux/actions/spinnerDataActions";
import { getLoggedInUserData, loginUser } from "../../redux/actions/userAction";
import { AppState } from "../../redux/types";
import { apiRequestHandler, insertSiteConfigRootStyles } from "../../utils/commonFunctions";
import { getSiteThemeConfigApi } from "../../redux/sagas/requests/commonRequest";
import { setSiteThemeConfigData } from "../../redux/actions/siteConfigDataActions";

export default function Auth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoader = useSelector((state: AppState) => state.loader.transparentLoader);
  const siteConfigData = useSelector((state: AppState) => state.siteThemeConfig);
  
  const auth = useSelector((state: AppState) => state.user.authStatus);
  const [showPasswordButton, setShowPasswordButton] = useState(false)

  // validations
  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .trim("Please remove leading and trailing whitespace")
      .strict(true)
      .min(3, "Username must be atleast 3 characters long")
      .max(30, "Username must be atmost 30 characters long")
      .required("Username is required")
      .matches(
        /^[a-zA-Z0-9\-_$@!.]{5,30}$/,
        "Username can have letters from a-z, A-Z and special characters including (-, _, $, @, !, .) and spaces are not allowed"
      ),
      
    password: Yup.string()
    .trim("Please remove leading and trailing whitespace")
    .strict(true)
    .min(8, "Password must be minimum 8 characters long.")
    .max(20, "Password must be atmost 20 characters long.")
    .required("Password is required."),
  });


  useEffect(() => {
    // make api call to get site config data
    const getSiteThemeConfigHandler = async () => {
      // if(!siteConfigData.colors.primary_default){
        const response = await apiRequestHandler(getSiteThemeConfigApi, undefined);
        const siteRootColors = response?.color;
        dispatch(setSiteThemeConfigData({
          logo_img: response?.logo?.location,
          colors: {
            primary_default: siteRootColors?.primary_default,
            primary_dark: siteRootColors?.primary_dark,
            primary_medium: siteRootColors?.primary_medium,
            primary_light: siteRootColors?.primary_light,
          }
        }));
        insertSiteConfigRootStyles({
          primary_default: siteRootColors?.primary_default,
          primary_dark: siteRootColors?.primary_dark,
          primary_medium: siteRootColors?.primary_medium,
          primary_light: siteRootColors?.primary_light, 
        })
      // } else {
      //   insertSiteConfigRootStyles({
      //     primary_default: siteConfigData.colors?.primary_default,
      //     primary_dark: siteConfigData.colors?.primary_dark,
      //     primary_medium: siteConfigData.colors?.primary_medium,
      //     primary_light: siteConfigData.colors?.primary_light, 
      //   })
      // }
  
    }
    getSiteThemeConfigHandler();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  // check auth
  useEffect(() => {
    if (auth) {
      navigate("/dashboard");
      // dispatch spinner data call
      dispatch(getSpinnerData());
      dispatch(getLoggedInUserData());
    }
  }, [navigate, dispatch, auth]);

  return (
    <>
      {/* loader */}
      <TransparentLoader loading={isLoader} />
      <div
        className="h-screen pt-28"
        style={{
          // backgroundImage: `url('../../assets/images/auth-background.webp')`,
          backgroundImage: `url('../../assets/images/auth-background.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* form */}
        <Formik
          enableReinitialize
          initialValues={{
            username: "",
            password: "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (values) {
              // submit login form
              dispatch(loginUser(values));
            }
          }}
        >
          {(props) => (
            <Form className="">
              <div className="mx-auto flex w-11/12 max-w-sm overflow-hidden rounded-lg bg-white shadow-lg lg:max-w-4xl">
                <div
                  className="hidden bg-cover lg:block lg:w-1/2"
                  style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1606660265514-358ebbadc80d?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=1575&q=80') `,
                  }}
                ></div>

                <div className="w-full px-6 py-12 md:px-8 lg:w-1/2">
                  <div className="mx-auto flex justify-center">
                    <img className="h-16 w-auto" src={siteConfigData.logo_img} alt="Company Logo" />
                    {/* <img className="h-auto w-36 object-contain" src="https://tqs-2023.s3.ap-south-1.amazonaws.com/brandingkite-config/logo_1.png" alt="Company Logo" /> */}
                  </div>

                  <p className="mt-3 text-center text-xl text-gray-600">Welcome back!</p>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="w-1/5 border-b lg:w-1/4"></span>

                    {/* <a href="#" className="text-xs text-center text-gray-500 uppercase hover:underline">or login
                                            with email</a> */}

                    <span className="w-1/5 border-b lg:w-1/4"></span>
                  </div>

                  <div className="mt-4">
                    <label htmlFor="username" className="tqs-input-label">
                      Username*
                    </label>
                    <Field
                      type="text"
                      name="username"
                      id="loginUsername"
                      className="tqs-input"
                      placeholder="Username"
                      maxLength={50}
                      autoComplete="off"
                    />
                    <ErrorMessage component="div" name="username" className="input-error" />
                  </div>

                  <div className="relative mt-4">
                    <label htmlFor="password" className="tqs-input-label">
                      Password*
                    </label>
                    <Field
                      type={showPasswordButton ? "text" : "password"}
                      name="password"
                      id="loginPassword"
                      className="tqs-input"
                      placeholder="Password"
                      maxLength={20}
                      autoComplete="off"
                    />
                    <span className="absolute right-2 top-6">
                      <IconButton aria-label="show password" onClick={() => setShowPasswordButton(!showPasswordButton)}>
                        {showPasswordButton ? <Eye size={20} /> : <EyeOff size={20} />}
                      </IconButton>
                    </span>
                    <ErrorMessage component="div" name="password" className="input-error" />
                  </div>

                  <div className="mt-6">
                    <button type="submit" className="btn-primary w-full">
                      Sign In
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
}
